import React, { useState } from 'react';
import { Header } from '../components/Header';
import { ProgressIndicator } from '../components/ProgressIndicator';
import { Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PaymentConfirmedModal } from './PaymentConfirmedScreen';
interface PaymentDetailsScreenProps {
  onNext: () => void;
  onBack: () => void;
  showConfirmation?: boolean;
  onConfirmationNext?: () => void;
  onConfirmationClose?: () => void;
}
export function PaymentDetailsScreen({
  onNext,
  onBack,
  showConfirmation = false,
  onConfirmationNext,
  onConfirmationClose
}: PaymentDetailsScreenProps) {
  const [selectedPayment, setSelectedPayment] = useState<'debit' | 'paypal'>('debit');
  return <div className="h-full flex flex-col overflow-hidden relative" data-id="element-103">
      <div className="flex-none" data-id="element-104">
        <Header title="Make Your Lease Payment" onBack={onBack} data-id="element-105" />
        <ProgressIndicator currentStep={1} data-id="element-106" />
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-2" data-id="element-107">
        {/* Room Card */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex gap-3 border border-gray-100" data-id="element-108">
          <img src="https://cdn.magicpatterns.com/uploads/v6PDEwrgMkkiNqhL35ww6B/331528273.jpg" alt="Room 302 interior" className="w-20 h-20 rounded-lg object-cover" data-id="element-109" />
          <div className="flex-1" data-id="element-110">
            <div className="flex items-center justify-between mb-1" data-id="element-111">
              <span className="font-semibold text-gray-900" data-id="element-112">Room 302</span>
              <span className="text-xs text-orange-500 font-medium" data-id="element-113">
                ⭐ 4.5
              </span>
            </div>
            <p className="text-xs text-gray-600 mb-2 leading-relaxed" data-id="element-114">
              Please complete all outstanding balance payments before your final
              date
            </p>
            <button className="text-xs text-[#0F4C3A] font-medium hover:underline" data-id="element-115">
              More Info
            </button>
          </div>
        </div>

        {/* Price Details */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6 border border-gray-100" data-id="element-116">
          <h3 className="font-semibold text-gray-900 mb-3" data-id="element-117">Price details</h3>
          <div className="space-y-2 text-sm" data-id="element-118">
            <div className="flex justify-between" data-id="element-119">
              <span className="text-gray-600" data-id="element-120">Remaining Debt</span>
              <span className="font-medium text-gray-900" data-id="element-121">$200</span>
            </div>
            <div className="flex justify-between" data-id="element-122">
              <span className="text-gray-600" data-id="element-123">Service Fee</span>
              <span className="font-medium text-gray-900" data-id="element-124">$30</span>
            </div>
            <div className="h-px bg-gray-100 my-3" data-id="element-125" />
            <div className="flex justify-between font-bold text-gray-900" data-id="element-126">
              <span data-id="element-127">Total</span>
              <span className="text-[#0F4C3A]" data-id="element-128">$230</span>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4 border border-gray-100" data-id="element-129">
          <h3 className="font-semibold text-gray-900 mb-3" data-id="element-130">Pay with</h3>
          <div className="space-y-4" data-id="element-131">
            <label className="flex items-center justify-between cursor-pointer group" data-id="element-132">
              <div className="flex items-center gap-3" data-id="element-133">
                <div className="relative flex items-center justify-center w-5 h-5" data-id="element-134">
                  <input type="radio" name="payment" checked={selectedPayment === 'debit'} onChange={() => setSelectedPayment('debit')} className="peer sr-only" data-id="element-135" />
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 peer-checked:border-[#0F4C3A] transition-colors" data-id="element-136" />
                  <motion.div initial={false} animate={{
                  scale: selectedPayment === 'debit' ? 1 : 0
                }} className="absolute w-2.5 h-2.5 bg-[#0F4C3A] rounded-full" data-id="element-137" />
                </div>
                <div data-id="element-138">
                  <div className="font-medium text-sm text-gray-900" data-id="element-139">
                    Debit card
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-140">
                    Visa, Mastercard and more
                  </div>
                </div>
              </div>
              <Info className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-141" />
            </label>

            <label className="flex items-center justify-between cursor-pointer group" data-id="element-142">
              <div className="flex items-center gap-3" data-id="element-143">
                <div className="relative flex items-center justify-center w-5 h-5" data-id="element-144">
                  <input type="radio" name="payment" checked={selectedPayment === 'paypal'} onChange={() => setSelectedPayment('paypal')} className="peer sr-only" data-id="element-145" />
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 peer-checked:border-[#0F4C3A] transition-colors" data-id="element-146" />
                  <motion.div initial={false} animate={{
                  scale: selectedPayment === 'paypal' ? 1 : 0
                }} className="absolute w-2.5 h-2.5 bg-[#0F4C3A] rounded-full" data-id="element-147" />
                </div>
                <div className="font-medium text-sm text-gray-900" data-id="element-148">Paypal</div>
              </div>
              <Info className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-149" />
            </label>
          </div>
        </div>
      </div>

      {/* Footer Button */}
      <div className="flex-none p-6 pt-2 pb-8 bg-[#F7F8F7]" data-id="element-150">
        <motion.button whileTap={{
        scale: 0.97
      }} onClick={onNext} className="w-full bg-[#0F4C3A] text-white py-4 rounded-xl font-semibold text-lg hover:bg-[#134E3A] transition-colors shadow-md" data-id="element-151">
          Next
        </motion.button>
      </div>

      {/* Payment Confirmed Pop-up */}
      <AnimatePresence data-id="element-152">
        {showConfirmation && <PaymentConfirmedModal onNext={() => onConfirmationNext?.()} onClose={() => onConfirmationClose?.()} data-id="element-153" />}
      </AnimatePresence>
    </div>;
}